// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import moment from 'moment-timezone';
import { Typography, Button, Card, Divider } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { Link as RouterLink } from 'react-router-dom';
import { ACCOUNT_PLAN_USAGE } from 'Utils/routes';
import { formatValue } from 'Utils/deliverables';
import { licenseFeePercentText } from 'Utils/licenseFee';
import { useAppSelector } from 'Hooks';
import PromotionBanner from 'Components/PromotionBanner';
import stylesDef from './styles';

moment.tz.setDefault('America/New_York');

const dollars = (value: number) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// The Billing card's plan block reads the account's one plan object, whatever the plan's origin (AUT-6571): the
// label, the price per credit, the overage rate and the license-fee line read the same for every account. The
// Express AI subscription keeps its own block.
export const PlanInfo = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const { subscriptionsLoading, expressAISubscription } = useAppSelector((state) => state.subscription);
  const promotion = useAppSelector((state) => state.account.activePromotion);
  const plan = useAppSelector((state) => state.account.org?.plan);

  const expressAISubscriptionExists = expressAISubscription && Object.keys(expressAISubscription).length > 0;
  const planLabel = plan?.label ? `${plan.label}${plan.tier ? ` · ${plan.tier} (Credit Ladder)` : ''}` : 'Plan';
  const feeAdded = plan?.licenseFee?.mode === 'added';
  // the account's own percent (AUT-6622), printed as entered — "10%", "12.5%"
  const feePercent = licenseFeePercentText(plan?.licenseFee?.percent);

  return (
    <Box>
      <Card sx={styles.subscriptionContent}>
        <Typography sx={styles.header} variant="h4">Accounts and Subscriptions</Typography>
        <Box>
          <>
            {plan && (
              <>
                <Divider sx={styles.divider} />
                <Typography sx={styles.planName} variant="h2">{planLabel}</Typography>
                {typeof plan.pricePerCredit === 'number' && (
                  <Typography sx={styles.planRate} variant="body1">
                    {`You are currently paying ${dollars(plan.pricePerCredit)} per credit.`}
                  </Typography>
                )}
                {plan.overage && (
                  <Typography sx={styles.planRate} variant="body2">
                    {`${dollars(plan.overage.applied)} per credit beyond the balance${feeAdded ? ` (includes the ${feePercent} license fee)` : ''}`}
                  </Typography>
                )}
                {feeAdded && (
                  <Typography sx={styles.planRate} variant="body2">{`+ ${feePercent} license fee on each order.`}</Typography>
                )}
                {promotion && <PromotionBanner promotion={promotion} variant="card" dateFormat="MMM D" />}
                <Box>
                  <RouterLink to={ACCOUNT_PLAN_USAGE} title="View Usage and Limits">
                    <Typography variant="h5" sx={styles.optionsButton}>
                      View Usage and Limits
                    </Typography>
                  </RouterLink>
                </Box>
              </>
            )}
            {expressAISubscriptionExists && (
              <>
                <Divider sx={styles.divider} />
                <Box sx={styles.planOptions}>
                  <Box>
                    <Typography sx={styles.planName} variant="h2">{expressAISubscription.name}</Typography>
                    {expressAISubscription.interval && <Typography sx={styles.planName} variant="h2">{` - ${expressAISubscription.interval}`}</Typography>}
                    {expressAISubscription.trial && <Typography sx={styles.planName} variant="h2"> - Trial</Typography>}
                  </Box>
                </Box>
              </>
            )}
          </>
          {!subscriptionsLoading && !plan && !expressAISubscriptionExists && (
            <Box sx={styles.planOptions}>
              <Box>
                <Typography sx={styles.planName} variant="h2">No active plan</Typography>
              </Box>
              <Button variant="outlined" onClick={() => { window.open('mailto:sales@airworks.io', '_blank'); }}>Upgrade plan</Button>
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default PlanInfo;
